/**
 * GMB Scoring System - Comprehensive business scoring based on multiple factors
 */

export class GMBScoringService {
  
  /**
   * Calculate comprehensive GMB score for a business
   */
  calculateGMBScore(
    business, 
    competitors = [], 
    cityName,
    keyword
  ) {
    
    const factors = {
      namePts: this.calculateNameScore(business, cityName),
      addrPts: this.calculateAddressScore(business, cityName, keyword),
      phonePts: this.calculatePhoneScore(business, competitors),
      descPts: this.calculateDescriptionScore(business),
      websitePts: this.calculateWebsiteScore(business),
      hoursPts: this.calculateHoursScore(business),
      labelPts: this.calculateLabelScore(business),
      catBasePts: this.calculateCategoryBaseScore(business),
      catExtraPts: this.calculateCategoryExtraScore(business),
      catCityPts: this.calculateCategoryCityScore(business, cityName),
      socialPts: this.calculateSocialScore(business),
      appointmentsPts: this.calculateAppointmentsScore(business),
      serviceAreaPts: this.calculateServiceAreaScore(business),
      bookApptPts: this.calculateBookAppointmentScore(business),
      qaPts: this.calculateQAScore(business),
      photosPts: this.calculatePhotosScore(business),
      reviewRatingPts: this.calculateReviewRatingScore(business),
      responseRatePts: this.calculateResponseRateScore(business),
      reviewsPts: this.calculateReviewsScore(business),
      velocityPts: this.calculateVelocityScore(business),
      feedPts: this.calculateFeedScore(business),
      productsPts: this.calculateProductsScore(business),
      servicesPts: this.calculateServicesScore(business)
    };

    // Calculate total score
    const score = Object.values(factors).reduce((sum, pts) => sum + (pts || 0), 0);
    
    return { score, factors };
  }

  calculateNameScore(business, cityName) {
    if (!business.name) return 0;
    let score = 50; // Base score for having a name
    
    // Bonus for city name in business name
    if (cityName && business.name.toLowerCase().includes(cityName.toLowerCase())) {
      score += 25;
    }
    
    return Math.min(score, 100);
  }

  calculateAddressScore(business, cityName, keyword) {
    if (!business.address) return 0;
    let score = 30; // Base score for having an address
    
    // Bonus for complete address
    if (business.address.includes(',')) score += 20;
    
    return Math.min(score, 50);
  }

  calculatePhoneScore(business, competitors) {
    if (!business.internationalPhoneNumber && !business.nationalPhoneNumber) return 0;
    return 40; // Base score for having a phone number
  }

  calculateDescriptionScore(business) {
    if (!business.description && !business.editorialSummary) return 0;
    const desc = business.description || business.editorialSummary || '';
    
    let score = 30; // Base score
    if (desc.length > 100) score += 20;
    if (desc.length > 200) score += 20;
    
    return Math.min(score, 70);
  }

  calculateWebsiteScore(business) {
    if (!business.websiteUri) return 0;
    return 50; // Base score for having a website
  }

  calculateHoursScore(business) {
    if (!business.openingHours || !business.openingHours.length) return 0;
    return 30; // Base score for having opening hours
  }

  calculateLabelScore(business) {
    // Mock implementation
    return 20;
  }

  calculateCategoryBaseScore(business) {
    if (!business.types || !business.types.length) return 0;
    return Math.min(business.types.length * 10, 50);
  }

  calculateCategoryExtraScore(business) {
    if (!business.types || business.types.length <= 1) return 0;
    return Math.min((business.types.length - 1) * 5, 25);
  }

  calculateCategoryCityScore(business, cityName) {
    // Mock implementation
    return 15;
  }

  calculateSocialScore(business) {
    // Mock implementation
    return 20;
  }

  calculateAppointmentsScore(business) {
    // Mock implementation
    return 15;
  }

  calculateServiceAreaScore(business) {
    // Mock implementation
    return 10;
  }

  calculateBookAppointmentScore(business) {
    // Mock implementation
    return 25;
  }

  calculateQAScore(business) {
    // Mock implementation
    return 30;
  }

  calculatePhotosScore(business) {
    if (!business.photoCount && business.photoCount !== 0) return 0;
    
    let score = 0;
    if (business.photoCount > 0) score += 20;
    if (business.photoCount > 5) score += 20;
    if (business.photoCount > 10) score += 20;
    if (business.photoCount > 20) score += 20;
    
    return Math.min(score, 80);
  }

  calculateReviewRatingScore(business) {
    if (!business.rating) return 0;
    return Math.min((business.rating / 5) * 100, 100);
  }

  calculateResponseRateScore(business) {
    // Mock implementation
    return 40;
  }

  calculateReviewsScore(business) {
    if (!business.userRatingCount) return 0;
    
    let score = 0;
    if (business.userRatingCount > 0) score += 20;
    if (business.userRatingCount > 10) score += 20;
    if (business.userRatingCount > 50) score += 20;
    if (business.userRatingCount > 100) score += 20;
    
    return Math.min(score, 80);
  }

  calculateVelocityScore(business) {
    // Mock implementation - would calculate review velocity
    return 25;
  }

  calculateFeedScore(business) {
    // Mock implementation
    return 20;
  }

  calculateProductsScore(business) {
    // Mock implementation
    return 15;
  }

  calculateServicesScore(business) {
    // Mock implementation
    return 25;
  }

  /**
   * Calculate penalty based on grid ranking performance
   * Uses the exact penalty calculation logic from CompetitorAnalysisModal
   */
  calculateRankingPenalty(competitor, totalGridPoints) {
    // Calculate ranking color counts for penalty calculation
    const greenCount = competitor.totalRanks.filter(rank => rank >= 1 && rank <= 3).length;
    const yellowCount = competitor.totalRanks.filter(rank => rank >= 4 && rank <= 10).length;
    const redCount = competitor.totalRanks.filter(rank => rank >= 11 && rank <= 20).length;
    const grayCount = totalGridPoints - competitor.gridAppearances; // Times not ranked
    
    // Grid-based penalty values
    const getPenaltyValues = (gridSize) => {
      if (gridSize === 49) return { yellow: 0.25, red: 0.75, gray: 1.0 }; // 7x7
      if (gridSize === 81) return { yellow: 0.15, red: 0.45, gray: 0.60 }; // 9x9
      if (gridSize === 144) return { yellow: 0.085, red: 0.25, gray: 0.34 }; // 12x12
      return { yellow: 0.25, red: 0.75, gray: 1.0 }; // Default to 7x7
    };
    
    const penalties = getPenaltyValues(totalGridPoints);
    
    // New penalty formula: (Yellow×penalty + Red×penalty + Gray×penalty) / 100
    const penaltyPercentage = (yellowCount * penalties.yellow + redCount * penalties.red + grayCount * penalties.gray) / 100;
    
    return {
      penaltyPercentage,
      breakdown: {
        greenCount,
        yellowCount,
        redCount,
        grayCount,
        penalties,
        gridCoverage: (competitor.gridAppearances / totalGridPoints) * 100
      }
    };
  }

  /**
   * Calculate final GMB score with ranking penalties applied
   */
  calculateFinalGMBScore(business, competitors, cityName, keyword, competitor, totalGridPoints) {
    // Get base GMB score
    const { score: baseScore, factors } = this.calculateGMBScore(business, competitors, cityName, keyword);
    
    // Calculate ranking penalty if competitor data is available
    if (competitor && competitor.totalRanks && totalGridPoints) {
      const penaltyData = this.calculateRankingPenalty(competitor, totalGridPoints);
      const finalScore = Math.max(0, baseScore * (1 - penaltyData.penaltyPercentage));
      
      return {
        baseScore,
        finalScore,
        penaltyPercentage: penaltyData.penaltyPercentage,
        penaltyBreakdown: penaltyData.breakdown,
        factors
      };
    }
    
    // Return base score if no penalty data available
    return {
      baseScore,
      finalScore: baseScore,
      penaltyPercentage: 0,
      penaltyBreakdown: null,
      factors
    };
  }
}
